import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth'
import { auth, googleProvider, ALLOWED_EMAILS } from './firebase'

type AuthState = 'loading' | 'signed-out' | 'unauthorized' | 'authorized'

interface AuthCtx {
  state: AuthState
  user: User | null
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
}

const Ctx = createContext<AuthCtx>({
  state: 'loading',
  user: null,
  signIn: async () => undefined,
  signOutNow: async () => undefined,
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [state, setState] = useState<AuthState>('loading')

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (!u) {
        setUser(null)
        setState('signed-out')
        return
      }
      setUser(u)
      const email = (u.email ?? '').toLowerCase()
      const ok = u.emailVerified && ALLOWED_EMAILS.includes(email)
      setState(ok ? 'authorized' : 'unauthorized')
    })
  }, [])

  async function signIn() {
    setState('loading')
    await signInWithPopup(auth, googleProvider)
  }

  async function signOutNow() {
    await signOut(auth)
  }

  return (
    <Ctx.Provider value={{ state, user, signIn, signOutNow }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  return useContext(Ctx)
}
